import { ChertoExperiments, type ExperimentConfig } from 'constants/experiments'

export type ExperimentEntry = [string, App.OptimizelyExperimentDetails]
export type ChertoExperimentEntry = [string, App.ChertoExperimentDetails | undefined, ExperimentConfig]
export type FeatureFlagEntry = [string, boolean]

const CHERTO_EXPERIMENTS_BY_ID = Object.values(ChertoExperiments).reduce<Record<string, ExperimentConfig>>(
  (experimentsById, experiment) => {
    experimentsById[experiment.id] = experiment
    return experimentsById
  },
  {},
)

/**
 * Formats the experiment title by splitting on underscores and hyphens.
 * e.g. 'multi_item_cart' -> 'Multi Item Cart', 'checkout-copy' -> 'Checkout Copy'
 */
export function formatExperimentTitle(experimentId: string): string {
  const experimentIdList = experimentId.split(/[_-]/)
  if (experimentIdList.length === 0) {
    return ''
  }
  return experimentIdList
    .filter(word => word.length > 0)
    .map(word => word[0].toUpperCase() + word.slice(1))
    .join(' ')
}

/** @deprecated Use formatExperimentTitle */
export const formatOptimizelyTitle = formatExperimentTitle

export function filterExperimentEntry(query: string) {
  return ([itemId]: ExperimentEntry | ChertoExperimentEntry | FeatureFlagEntry) => {
    const itemName = formatExperimentTitle(itemId)
    return (itemId.toLowerCase().includes(query.toLowerCase()) || itemName.toLowerCase().includes(query.toLowerCase()))
  }
}

/** @deprecated Use filterExperimentEntry */
export const filterOptimizelyEntry = filterExperimentEntry

export function getChertoExperimentConfig(experimentId: string): ExperimentConfig | undefined {
  return CHERTO_EXPERIMENTS_BY_ID[experimentId]
}

export function getChertoExperimentCohortKeys(variations: ReadonlyArray<string>): Array<string> {
  return ['off', ...variations]
}

export function getChertoSelectedCohort(
  experiment: App.ChertoExperimentDetails | undefined,
  cohortKeys: Array<string>,
): string {
  if (!experiment?.enabled) {
    return 'off'
  }

  if (cohortKeys.includes(experiment.variationKey)) {
    return experiment.variationKey
  }

  return cohortKeys[0] ?? experiment.variationKey
}

export function getExperimentCohortKeys(experiment: App.OptimizelyExperimentDetails): Array<string> {
  const variationMap = experiment.variation ?? {}
  const cohortKeys = Object.keys(variationMap).sort((a, b) => a.localeCompare(b))

  if (cohortKeys.length === 0) {
    return ['off', 'on']
  }

  return ['off', ...cohortKeys]
}

export function getSelectedCohort(
  experiment: App.OptimizelyExperimentDetails,
  cohortKeys: Array<string>,
): string {
  if (!experiment.enabled) {
    return 'off'
  }

  if (cohortKeys.includes(experiment.variationKey)) {
    return experiment.variationKey
  }

  if (cohortKeys.includes('on')) {
    return 'on'
  }

  return cohortKeys[0] ?? experiment.variationKey
}

export function getChertoExperimentReportUrl(experimentId: string): string {
  return `https://app.getcherto.com/experiments/${experimentId}`
}
